import {useAsyncCallback} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {useTenant} from "../../context/tenant";
import {OrderRequest, useSiteApi} from "../../data/sites";

/**
 * Shared submit handler for both checkout flows (multi-step and simplified): submits the order and navigates to the
 * payment page on success. A failure is exposed via `error` so the caller can render the OrderSubmitError alert; the
 * rejection is swallowed here because that error state is the user-facing signal. Navigation runs outside the catch,
 * so a post-submit failure is not silently mislabeled as a handled submit error.
 */
export function useOrderSubmit() {
	const navigate = useNavigate();
	const {tenant} = useTenant();
	const {submitOrder} = useSiteApi();

	const {loading, error, execute} = useAsyncCallback(async (siteId: string, cart: OrderRequest) => {
		if (!tenant) {
			throw new Error("No tenant selected.");
		}
		const booking = await submitOrder(tenant.id, siteId, cart);
		if (!booking?.id) {
			// e.g. an intermediary answering 2xx without a JSON body — reject so the error alert shows
			throw new Error("Booking creation returned no booking.");
		}
		return booking;
	});

	async function submitOrderAndPay(siteId: string, cart: OrderRequest) {
		let booking;
		try {
			booking = await execute(siteId, cart);
		}
		catch (err) {
			// surfaced to the user via the error state → OrderSubmitError
			console.error("Failed to create booking", err);
			return;
		}
		navigate(`/${tenant?.id}/bookings/${booking.id}/payment`);
	}

	return {loading, error, submitOrderAndPay};
}
